import type { ChartTooltipFormatter, XAxisTickFormatter } from './chart-types';

/**
 * Declarative description of how to render a numeric value as text.
 *
 * Charts have always accepted formatter *functions*, which works for callers
 * that write JSX by hand and fails for callers whose props arrive as data —
 * a saved document, a cached API response, a server-rendered payload. A
 * function cannot survive `JSON.stringify`, so those callers were forced to
 * resolve a stored format at the last moment, in whatever layer happened to
 * sit between the data and the chart.
 *
 * This spec is the serializable alternative, and it deliberately carries
 * primitive operations rather than named formats: `'currency'` or `'gpa'`
 * would put a product's vocabulary inside a UI library, and that vocabulary
 * is exactly what drifts. Callers keep their own named formats and translate
 * them into these operations, which are applied in order — scale, round,
 * group, suffix.
 */
export interface NumberFormatSpec {
  /**
   * Multiplied in before anything else. Use `100` for ratios stored as
   * decimals so `0.756` can render as `76%`.
   */
  readonly scale?: number;
  /** Round to a whole number. Takes precedence over `fractionDigits`. */
  readonly roundToInteger?: boolean;
  /**
   * Exact number of fraction digits, padded if necessary. Omit both this and
   * `roundToInteger` to render the number as-is.
   */
  readonly fractionDigits?: number;
  /** Insert en-US thousands separators. */
  readonly grouping?: boolean;
  /**
   * Show a leading `+` on positive values. `'exceptZero'` leaves zero
   * unsigned, which is what a movement figure wants: `+3.1%` and `-3.1%` read
   * as changes, while `+0.0%` reads as a change that did not happen.
   */
  readonly signDisplay?: 'exceptZero';
  /** Appended verbatim, including any leading space. */
  readonly suffix?: string;
}

/**
 * A number together with how to render it, for props that take display text.
 *
 * A caller whose props arrive as data has the number and the format but not the
 * text, and formatting early forces every layer in between to carry a string it
 * cannot interpret. Passing both lets the component render the text and still
 * read the number — which is why `null` is a member here rather than something
 * the caller turns into a placeholder itself.
 */
export interface DescribedNumber {
  readonly value: number | null;
  readonly format?: NumberFormatSpec;
}

/** Display text, or the number and format to render it from. */
export type DisplayValue = string | DescribedNumber;

/** A value formatter, either resolved or described. */
export type ValueFormat = NumberFormatSpec | ((value: number) => string);

/** An X-axis tick formatter, either resolved or described. */
export type TickFormat = NumberFormatSpec | XAxisTickFormatter;

/**
 * Renders each tooltip value alongside a signed delta read from another
 * column of the same row: `"42.0% (+3.1%)"`. Rows missing the column, or
 * carrying a non-numeric one, render the value alone.
 *
 * `unit` is appended to the delta only. The delta is shown as stored, so a
 * column holding `3.1` for "3.1%" wants `unit: '%'`, while a column holding
 * a raw count wants no unit.
 */
export interface ChartTooltipDeltaSpec {
  readonly kind: 'value-with-delta';
  /** Row key holding the delta. */
  readonly deltaColumn: string;
  /** Appended to the delta only. */
  readonly unit?: string;
}

/** A tooltip formatter, either resolved or described. */
export type TooltipFormat = ChartTooltipDeltaSpec | ChartTooltipFormatter;

function isSpec(format: unknown): format is NumberFormatSpec {
  return typeof format === 'object' && format !== null;
}

function isDeltaSpec(format: unknown): format is ChartTooltipDeltaSpec {
  return (
    typeof format === 'object' &&
    format !== null &&
    (format as ChartTooltipDeltaSpec).kind === 'value-with-delta'
  );
}

/**
 * A `+` for positive values under `signDisplay: 'exceptZero'`. Negatives and
 * zero need nothing: the number already carries its own minus sign, and zero is
 * deliberately unsigned.
 */
function signPrefix(spec: NumberFormatSpec, scaled: number): string {
  return spec.signDisplay === 'exceptZero' && scaled > 0 ? '+' : '';
}

function applySpec(spec: NumberFormatSpec, value: number): string {
  const scaled = spec.scale === undefined ? value : value * spec.scale;
  const suffix = spec.suffix ?? '';
  const sign = signPrefix(spec, scaled);

  if (spec.roundToInteger) {
    const rounded = Math.round(scaled);
    return `${sign}${
      spec.grouping ? rounded.toLocaleString('en-US') : String(rounded)
    }${suffix}`;
  }

  if (spec.fractionDigits === undefined) {
    return `${sign}${String(scaled)}${suffix}`;
  }

  return `${sign}${
    spec.grouping
      ? scaled.toLocaleString('en-US', {
          minimumFractionDigits: spec.fractionDigits,
          maximumFractionDigits: spec.fractionDigits,
        })
      : scaled.toFixed(spec.fractionDigits)
  }${suffix}`;
}

/**
 * Turn a {@link ValueFormat} into the callable the chart internals use.
 * Functions pass through untouched, so existing callers are unaffected.
 */
export function resolveValueFormat(
  format: ValueFormat | undefined,
): ((value: number) => string) | undefined {
  if (format === undefined) {
    return undefined;
  }
  if (typeof format === 'function') {
    return format;
  }
  return (value: number) => applySpec(format, value);
}

/**
 * Turn a {@link TickFormat} into an {@link XAxisTickFormatter}.
 *
 * Tick values are `number | string` because a category axis passes its raw
 * row keys through. A spec describes numeric formatting, so a string tick is
 * formatted only when it parses cleanly as a number and is otherwise returned
 * unchanged — a numeric format chosen on a category axis degrades to plain
 * labels instead of printing `NaN`.
 */
export function resolveTickFormat(
  format: TickFormat | undefined,
): XAxisTickFormatter | undefined {
  if (format === undefined) {
    return undefined;
  }
  if (typeof format === 'function') {
    return format;
  }
  return (value: number | string): string => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return applySpec(format, value);
    }
    const coerced = typeof value === 'string' ? Number(value) : Number.NaN;
    return Number.isFinite(coerced)
      ? applySpec(format, coerced)
      : String(value);
  };
}

/**
 * Turn a {@link TooltipFormat} into a {@link ChartTooltipFormatter}.
 *
 * `valueFormat` is the chart's own resolved value formatter, so a described
 * tooltip renders its value exactly like the matching axis does.
 */
export function resolveTooltipFormat(
  format: TooltipFormat | undefined,
  valueFormat: ((value: number) => string) | undefined,
): ChartTooltipFormatter | undefined {
  if (format === undefined) {
    return undefined;
  }
  if (typeof format === 'function') {
    return format;
  }
  if (!isDeltaSpec(format)) {
    return undefined;
  }

  const { deltaColumn, unit = '' } = format;

  return (value: number, _name: string, row: Record<string, unknown>) => {
    const formattedValue = valueFormat ? valueFormat(value) : String(value);

    const raw = row[deltaColumn];
    if (raw === undefined || raw === null) {
      return formattedValue;
    }

    const delta = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(delta)) {
      return formattedValue;
    }

    const sign = delta >= 0 ? '+' : '-';
    return `${formattedValue} (${sign}${Math.abs(delta).toFixed(1)}${unit})`;
  };
}

/**
 * Placeholder for a described number with no value, matching what a dash
 * already means on a tile: the metric exists, this period has no figure for it.
 */
export const NO_VALUE = '—';

function isDescribedNumber(value: unknown): value is DescribedNumber {
  return (
    typeof value === 'object' &&
    value !== null &&
    'value' in (value as DescribedNumber)
  );
}

/** The text to render for a {@link DisplayValue}. */
export function resolveDisplayValue(value: DisplayValue): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value.value === null || !Number.isFinite(value.value)) {
    return NO_VALUE;
  }
  return value.format === undefined
    ? String(value.value)
    : applySpec(value.format, value.value);
}

/**
 * The number behind a {@link DisplayValue}, when it was given as one.
 *
 * A described number is read directly; a string has to be parsed back, which is
 * lossy and locale-bound. Callers that need the sign should prefer describing
 * the value so this returns the real number instead of a reconstruction.
 */
export function displayValueNumber(value: DisplayValue): number | null {
  if (isDescribedNumber(value)) {
    return value.value !== null && Number.isFinite(value.value)
      ? value.value
      : null;
  }
  return null;
}

/** True when a prop carries a spec rather than a resolved formatter. */
export function isNumberFormatSpec(
  format: ValueFormat | TickFormat | undefined,
): format is NumberFormatSpec {
  return isSpec(format) && typeof format !== 'function';
}
